import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeNavButton } from '../../components/NavButtons';
import { getStudentClasses } from '../../services/classService';
import { getStudentClassContents } from '../../services/classContentService';
import {
  flattenClassSubjects,
  formatClassSubjectOptionLabel,
} from '../../utils/classSubjectUtils';
import { focusTheme, getListCardProps } from '../../utils/focusTheme';

function ArrowCircleIcon({ className = 'text-teal-500' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M8.63612 5.41078L6.69336 7.21477C6.38802 7.4983 6.38802 7.98157 6.69336 8.2651L8.63612 10.0691M7.70447 0.537475C3.74645 0.537475 0.537843 3.74608 0.537843 7.7041C0.537843 11.6621 3.74645 14.8707 7.70447 14.8707C11.6625 14.8707 14.8711 11.6621 14.8711 7.7041C14.8711 3.74608 11.6625 0.537475 7.70447 0.537475Z" stroke="currentColor" strokeWidth="1.07499" strokeLinecap="round" />
    </svg>
  );
}

function formatContentDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}


function getContentTypeConfig(contentType) {
  return focusTheme.contentType[contentType] || focusTheme.contentType.other;
}

function ContentsPage() {
  const [contents, setContents] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [selectedClassSubjectId, setSelectedClassSubjectId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const typeCounts = useMemo(
    () => contents.reduce((counts, content) => {
      const type = content.contentType || 'other';
      counts[type] = (counts[type] || 0) + 1;
      return counts;
    }, {}),
    [contents]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadClasses() {
      try {
        const classItems = await getStudentClasses();

        if (isMounted) {
          setClassSubjects(flattenClassSubjects(classItems));
        }
      } catch {
        if (isMounted) {
          setClassSubjects([]);
        }
      }
    }

    loadClasses();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadContents() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const contentItems = await getStudentClassContents({
          classSubjectId: selectedClassSubjectId || undefined,
          status: 'published',
        });

        if (isMounted) {
          setContents(contentItems);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت محتواها وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadContents();

    return () => {
      isMounted = false;
    };
  }, [selectedClassSubjectId]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p
            style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
            className="px-6 py-1 rounded-[10px] text-[15px] text-white"
          >
            محتوای آموزشی
          </p>

          <img className="h-[50px]" src="/images/content.webp" alt="" />
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className="text-[11px]">لیست محتواهای آموزشی</p>
        <p className="text-[11px] font-light">
          فایل‌ها، تصاویر، ویدیوها و لینک‌هایی که معلم برایت ارسال کرده را اینجا ببین.
        </p>
      </div>

      {classSubjects.length > 0 && (
        <div
          className="rounded-[8px] p-2"
          style={{ background: focusTheme.sectionBg }}
        >
          <label className="block text-[11px] text-slate-700" htmlFor="class-subject-filter">
            فیلتر بر اساس درس کلاس
          </label>
          <select
            id="class-subject-filter"
            value={selectedClassSubjectId}
            onChange={(event) => setSelectedClassSubjectId(event.target.value)}
            className="mt-2 w-full rounded-[6px] border border-teal-200 bg-white px-3 py-2 text-[12px] text-slate-800 outline-none"
          >
            <option value="">همه درس‌ها</option>
            {classSubjects.map((classSubject) => (
              <option key={classSubject.id} value={classSubject.id}>
                {formatClassSubjectOptionLabel(classSubject)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div
        className="grid grid-cols-2 gap-2 rounded-[8px] p-2"
        style={{ background: focusTheme.sectionBg }}
      >
        <div
          className="rounded-[6px] px-3 py-2 text-teal-800"
          style={{ background: focusTheme.stat.remaining }}
        >
          <p className="text-[11px] font-medium">همه محتواها</p>
          <p className="mt-1 text-[17px] font-bold">{contents.length}</p>
        </div>

        <div
          className="rounded-[6px] px-3 py-2 text-emerald-800"
          style={{ background: focusTheme.stat.completed }}
        >
          <p className="text-[11px] font-medium">انواع محتوا</p>
          <p className="mt-1 text-[13px] font-bold">
            {Object.keys(typeCounts).length || 0} نوع
          </p>
        </div>
      </div>

      <div
        className="flex flex-col gap-2 rounded-[8px] p-2"
        style={{ background: focusTheme.sectionBg }}
      >
        {isLoading ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center text-[12px] text-slate-500">
            در حال دریافت محتواها...
          </div>
        ) : errorMessage ? (
          <div className="rounded-[8px] bg-white px-4 py-5 text-center">
            <p className={`text-[12px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`mt-3 rounded-[6px] px-5 py-1 text-[12px] font-bold ${focusTheme.button.retry}`}
            >
              تلاش دوباره
            </button>
          </div>
        ) : contents.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">فعلا محتوایی نداری</p>
            <p className="mt-2 text-[11px] text-slate-500">
              وقتی معلم محتوای جدیدی منتشر کند، همینجا نمایش داده می‌شود.
            </p>
          </div>
        ) : (
          contents.map((content, index) => {
            const typeConfig = getContentTypeConfig(content.contentType);
            const cardProps = getListCardProps('active');
            const status = cardProps.status;

            return (
              <Link
                key={content.id}
                to={`/contents/${content.id}`}
                className={cardProps.className}
                style={cardProps.style}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[5px] text-[12px] font-bold ${status.badgeClass}`}>
                      {index + 1}
                    </span>

                    <p className="min-w-0 truncate text-[14px] font-semibold text-slate-900">
                      {content.title || 'بدون عنوان'}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${typeConfig.badgeClass}`}>
                      {typeConfig.label}
                    </span>
                    <ArrowCircleIcon className={status.iconClass} />
                  </div>
                </div>

                {content.description?.trim() ? (
                  <p className="line-clamp-2 text-[13px] leading-6 text-slate-700">
                    {content.description}
                  </p>
                ) : null}

                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className={focusTheme.meta.chip}>
                    {formatContentDate(content.createdAt)}
                  </div>
                  <div className={`${focusTheme.meta.status} ${status.labelClass}`}>
                    مشاهده محتوا
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ContentsPage;
